import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const POLL_DELAY = 2000;
const MAX_POLLS = 8;

export default function ConsultSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", payment_status: null, consult: null });
  const [attempts, setAttempts] = useState(0);
  const [err, setErr] = useState("");

  const poll = useCallback(async () => {
    if (!sessionId) { setErr("Missing session_id"); return; }
    try {
      const r = await api.get(`/consults/${sessionId}/status`);
      setState(r.data);
      if (r.data.payment_status === "paid" || r.data.status === "expired") return;
      if (attempts + 1 < MAX_POLLS) {
        setTimeout(() => setAttempts((a) => a + 1), POLL_DELAY);
      }
    } catch (e) {
      setErr(formatErr(e));
    }
  }, [sessionId, attempts]);

  useEffect(() => { poll(); }, [poll]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";
  const pending = !paid && !expired && !err;

  return (
    <div className="login-shell" data-testid="consult-success-page">
      <div className="login-card" style={{ maxWidth: 480, textAlign: "center" }}>
        {pending && (
          <>
            <Loader2 size={28} className="animate-spin" style={{ color: "var(--copper)", margin: "0 auto 14px" }} />
            <div className="intake-eyebrow">Confirming payment</div>
            <h2 className="serif" style={{ fontSize: 24, fontWeight: 600, margin: "6px 0 8px" }}>One moment…</h2>
            <p style={{ fontSize: 13.5, color: "var(--muted)" }}>We're verifying your consultation booking with Stripe. This usually takes a few seconds.</p>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 size={36} style={{ color: "#2f5a36", margin: "0 auto 14px" }} />
            <div className="intake-eyebrow" style={{ color: "#2f5a36" }}>Consultation booked</div>
            <h2 className="serif" style={{ fontSize: 26, fontWeight: 600, margin: "6px 0 12px", letterSpacing: "-0.02em" }}>You're on Reza's calendar.</h2>
            <p style={{ fontSize: 13.5, color: "var(--muted)", lineHeight: 1.7 }}>
              Reza Chawla will reach out within <strong>2 business hours</strong> to confirm your time slot, using the contact details you provided.
              {state.consult?.preferred_time ? <> Preferred window: <strong>{state.consult.preferred_time}</strong>.</> : null}
            </p>
            <div style={{ marginTop: 18, padding: 14, background: "#fff8f1", borderLeft: "2px solid var(--copper)", textAlign: "left", fontSize: 12.5, color: "var(--ink-soft)" }}>
              A receipt has been emailed to <strong>{state.consult?.email || "your inbox"}</strong>. If you don't hear from Reza within 2 hours, text directly: <a href="tel:+17132912875" style={{ color: "var(--copper)", fontWeight: 600 }}>(713) 291-2875</a>.
            </div>
          </>
        )}
        {expired && (
          <>
            <Clock size={32} style={{ color: "var(--muted)", margin: "0 auto 14px" }} />
            <div className="intake-eyebrow">Session expired</div>
            <h2 className="serif" style={{ fontSize: 22, fontWeight: 600, margin: "6px 0 10px" }}>The checkout session timed out.</h2>
            <p style={{ fontSize: 13.5, color: "var(--muted)" }}>No charge was made. You can try again.</p>
          </>
        )}
        {err && (
          <>
            <XCircle size={32} style={{ color: "#b73b2a", margin: "0 auto 14px" }} />
            <div className="intake-eyebrow" style={{ color: "#b73b2a" }}>Status check failed</div>
            <p style={{ fontSize: 13, color: "var(--muted)" }}>{err}</p>
          </>
        )}

        <Link to="/" style={{ display: "inline-block", marginTop: 22, fontFamily: "var(--mono)", fontSize: 11, letterSpacing: ".14em", color: "var(--muted)", textDecoration: "none" }} data-testid="consult-back-home">← BACK TO TEXAS FIRM MATCH</Link>
      </div>
    </div>
  );
}
